import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertTriggers,
  convertQuestionMappings,
  convertNotRequired,
} from '../utils/convertVisibleIf'

const BIRTH_DATE_PLACEHOLDER = '${A:birthday}'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
  birthDate,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
    // useUserIdLogin: true,
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
    21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39,
    40,
  ]
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 防止閃閃
  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
              console.log('✅ 強制加上 mobile class 避免抖動')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 以下皆為 1-based 寫法，convertVisibleIf / 字串插值會由工具自動 -1 還原為 0-based
  // 民國年(q8)、西元年齡拆成 q12+q13、性別(q14)
  const isWantsPregnant =
    '(({q8} >= 16 and {q8} <= 47) or ({q12} >= 16 and {q12} <= 47) or ({q13} >= 16 and {q13} <= 47)) and ({q14} = 2) and ({q36} = 2 or {q36} = 3 or {q37} = 2) and ({q184} = 2 or {q184} = "other") and ({q187} = 4 or {q187} = 5 or {q187} = 6)'
  const isPostmenopausal =
    '(({q8} >= 6 and {q8} <= 25) or ({q12} >= 6 and {q12} <= 25) or ({q13} >= 6 and {q13} <= 25)) and ({q14} = 2) and ({q187} = 1)'
  const isRecentDrinker = '({q259} = 1 or {q259} = 2)'

  const visibleIfConditionsInit = convertVisibleIf({
    // A1_1(q5)=2(不是) → 顯示 A1_0a(q6)
    q6: '{q5} = 2',
    q7: '{q6} = 1',
    q8: '({q6} = 1)',
    q9: '({q6} = 1)',
    q10: '({q6} = 1)',
    q11: '({q6} = 2)',
    // A1_1(q5)=1(是)/3(不清楚) 也直接顯示 A1_1b-1(q12)
    q12: '({q11} = 2) or ({q5} = 1) or ({q5} = 3)',
    q13: '({q11} = 1)',
    q16: '{q15} = 8 or {q15} = 11 or {q15} = 14 or {q15} = 16 or {q15} = 18 or {q15} = 19',
    q18: '{q17} = 1',
    q19: '{q17} = 2',
    // A5
    q21: '{q20} = 1',
    q22: '({q20} = 1) and ({q21} anyof [1])',
    q23: '({q20} = 1) and ({q21} anyof [2])',
    q24: '({q20} = 1) and ({q21} anyof [3])',
    q25: '({q20} = 1) and ({q21} anyof [4])',
    q26: '({q20} = 1) and ({q21} anyof [5])',
    q27: '({q20} = 1) and ({q21} anyof [6])',
    q28: '({q20} = 1) and ({q21} anyof [7])',
    q29: '({q20} = 1) and ({q21} anyof [8])',
    q30: '({q20} = 1) and ({q21} anyof [9])',
    q31: '({q20} = 1) and ({q21} anyof [10])',
    q32: '({q20} = 1) and ({q21} anyof [11])',
    q33: '({q20} = 1) and ({q21} anyof [12])',
    q34: '({q20} = 1) and ({q21} anyof [13])',
    q35: '{q20} = 1',
    // A6
    q37: '{q36} = 1 or {q36} = 4 or {q36} = 5 or {q36} = 6 or {q36} = 7',
    q38: '{q36} = 2 or {q36} = 3 or {q37} = 2',
    q39: '({q36} = 2 or {q36} = 3 or {q37} = 2) and ({q38} = 1)',
    q40: '({q36} = 2 or {q36} = 3 or {q37} = 2) and ({q38} = 2 or {q38} = 3)',
    q41: '({q36} = 2 or {q36} = 3 or {q37} = 2) and ({q38} = 1 or {q38} = 2 or {q38} = 3)',
    q42: '{q36} = 2 or {q36} = 3 or {q37} = 2',
    q43: '({q36} = 2 or {q36} = 3 or {q37} = 2) and ({q42} = 8 or {q42} = 11 or {q42} = 14 or {q42} = 16 or {q42} = 18 or {q42} = 19)',
    // A7
    q45: '{q44} = 1',
    q46: '{q44} = 2 or {q44} = 3',
    q48: '{q47} = 1',
    q49: '{q47} = 2 or {q47} = 3',
    // B3
    q53: '{q52} = 2',
    q54: '({q52} = 2) and ({q53} = 2)',
    q55: '({q52} = 2) and ({q53} = 2) and ({q54} = 2)',
    q56: '{q52} = 2',
    // B4
    q58: '{q57} = 2',
    q59: '({q57} = 2) and ({q58} = 2)',
    q60: '({q57} = 2) and ({q58} = 2)',
    q61: '({q57} = 2) and ({q58} = 2) and ({q60} = 2)',
    q62: '{q57} = 2',
    q63: '{q57} = 2',
    q64: '{q57} = 2',
    q65: '({q57} = 2) and ({q64} = 2)',
    q66: '{q57} = 2',
    q67: '{q57} = 2',
    q68: '({q57} = 2) and ({q67} = 2)',
    q69: '{q57} = 2',
    q70: '({q57} = 2) and ({q69} = 2)',
    // B5
    q72: '{q71} = 2',
    q73: '({q71} = 2) and ({q72} = 2)',
    q74: '({q71} = 2) and ({q72} = 2) and ({q73} = 2)',
    q75: '{q71} = 2',
    // B6
    q76: '({q52} = 2) or ({q57} = 2) or ({q71} = 2)',
    // B7：中風（B7=2 是 → 顯示後續；B7b 選 1 民國年月 → 顯示 B7b-11(年)、B7b-12(月)；選 2 年前 → 顯示 B7b-2）
    q78: '{q77} = 2',
    q79: '{q77} = 2',
    q80: '{q77} = 2',
    q81: '({q77} = 2) and ({q80} = 1)',
    q82: '({q77} = 2) and ({q80} = 1)',
    q83: '({q77} = 2) and ({q80} = 2)',
    q84: '{q77} = 2',
    q85: '({q77} = 2) and ({q84} = 2)',
    // B8 氣喘（q86=B8）：B8b 只有 B8a=是(2) 才顯示
    q87: '{q86} = 2',
    q88: '({q86} = 2) and ({q87} = 2)',
    q89: '{q86} = 2',
    q90: '{q86} = 2',
    q91: '{q86} = 2',
    q92: '{q86} = 2',
    q93: '{q86} = 2',
    q94: '{q86} = 2',
    // B9 COPD（q99=B9e）
    q100: '{q99} = 2',
    // B10 B/C 肝炎（q101=B10_1, q108=B10_2）
    q102: '{q101} = 2',
    q103: '{q101} = 2',
    q104: '({q101} = 2) and ({q103} = 2)',
    q105: '({q101} = 2) and ({q103} = 2) and ({q104} = 1)',
    q106: '({q101} = 2) and ({q103} = 2) and ({q104} = 2)',
    q107: '({q101} = 2) and ({q103} = 2)',
    q109: '{q108} = 2',
    q110: '{q108} = 2',
    q111: '({q108} = 2) and ({q110} = 2)',
    q112: '({q108} = 2) and ({q110} = 2) and ({q111} = 1)',
    q113: '({q108} = 2) and ({q110} = 2) and ({q111} = 2)',
    q114: '({q108} = 2) and ({q110} = 2)',
    q115: '({q108} = 2) and ({q110} = 2)',
    // B11 腎臟病（q119=B11, q123=B11c）
    q120: '{q119} = 2',
    q121: '{q119} = 2',
    q122: '({q119} = 2) and ({q121} = 2)',
    q123: '{q119} = 2',
    // B12 男(q124)/女(q125) 疾病主題索引（matrix：列 = 疾病、欄 = 沒有(1)/有(2)）
    q124: '{q14} = 1 or {q14} = 3',
    q125: '{q14} = 2',
    // 癌症選單：1=主要癌症、2=第二嚴重癌症（21 = 沒有第二種）
    q126: '({q124.6} = 2) or ({q125.6} = 2)',
    q127: '({q124.6} = 2) or ({q125.6} = 2)',
    // B12 心臟（疾病列 1）
    q128: '({q124.1} = 2) or ({q125.1} = 2)',
    q129: '({q124.1} = 2) or ({q125.1} = 2)',
    q130: '({q124.1} = 2) or ({q125.1} = 2)',
    // B12 痛風（疾病列 2）
    q131: '({q124.2} = 2) or ({q125.2} = 2)',
    q132: '({q124.2} = 2) or ({q125.2} = 2)',
    q133: '({q124.2} = 2) or ({q125.2} = 2)',
    // B12 胃潰瘍/十二指腸潰瘍（列 3）
    q134: '({q124.3} = 2) or ({q125.3} = 2)',
    q135: '({q124.3} = 2) or ({q125.3} = 2)',
    q136: '({q124.3} = 2) or ({q125.3} = 2)',
    // B12 肝膽疾病（列 4）
    q137: '({q124.4} = 2) or ({q125.4} = 2)',
    q138: '({q124.4} = 2) or ({q125.4} = 2)',
    q139: '({q124.4} = 2) or ({q125.4} = 2)',
    // B12 骨質疏鬆（列 5，僅 2 題）
    q140: '({q124.5} = 2) or ({q125.5} = 2)',
    q141: '({q124.5} = 2) or ({q125.5} = 2)',
    // B12 主要癌症（列 6）
    q142: '({q124.6} = 2) or ({q125.6} = 2)',
    q143: '({q124.6} = 2) or ({q125.6} = 2)',
    q144: '({q124.6} = 2) or ({q125.6} = 2)',
    // B12 第二嚴重癌症（僅當 q127 選 1~20）
    q145: '(({q124.6} = 2) or ({q125.6} = 2)) and ({q127} != 21)',
    q146: '(({q124.6} = 2) or ({q125.6} = 2)) and ({q127} != 21)',
    q147: '(({q124.6} = 2) or ({q125.6} = 2)) and ({q127} != 21)',
    // B12 關節炎（列 7）
    q148: '({q124.7} = 2) or ({q125.7} = 2)',
    q149: '({q124.7} = 2) or ({q125.7} = 2)',
    q150: '({q124.7} = 2) or ({q125.7} = 2)',
    // B12 憂鬱症（列 8）
    q151: '({q124.8} = 2) or ({q125.8} = 2)',
    q152: '({q124.8} = 2) or ({q125.8} = 2)',
    q153: '({q124.8} = 2) or ({q125.8} = 2)',
    // B12 思覺失調（列 9）
    q154: '({q124.9} = 2) or ({q125.9} = 2)',
    q155: '({q124.9} = 2) or ({q125.9} = 2)',
    q156: '({q124.9} = 2) or ({q125.9} = 2)',
    // B12 其他精神（列 10）
    q157: '({q124.10} = 2) or ({q125.10} = 2)',
    q158: '({q124.10} = 2) or ({q125.10} = 2)',
    q159: '({q124.10} = 2) or ({q125.10} = 2)',
    // B12 攝護腺【男，列 11 of 男 matrix】
    q160: '({q124.11} = 2) and ({q14} = 1)',
    q161: '({q124.11} = 2) and ({q14} = 1)',
    q162: '({q124.11} = 2) and ({q14} = 1)',
    // B12 子宮卵巢【女，列 11 of 女 matrix】
    q163: '({q125.11} = 2) and ({q14} = 2)',
    q164: '({q125.11} = 2) and ({q14} = 2)',
    q165: '({q125.11} = 2) and ({q14} = 2)',
    // B13（NEW: q167=B13b, q168=B13c, q169=B13c_a, q170=B13c_b, q171=B13d, q172=B13d_1）
    q169: '{q168} = 1',
    q170: '{q168} = 2 or {q168} = 3 or {q168} = 4',
    q171: '({q169} = 2 or {q169} = 3 or {q169} = 4) or ({q170} = 2 or {q170} = 3 or {q170} = 4)',
    q172: '{q171} = 2',
    // B14 牙齒（q173=B14: 1沒有→跳 q178 B14b；2有→顯示 q174 顆數；2有/3全口→顯示 q175 假牙）
    // q175 (B14a) 選「沒有任何假牙」(none) → 跳 q178 B14b，q176/q177 自然不顯示
    // q176 (B14a_1 部分/全口活動式) 只在 q175 有「活動式假牙」(option 1) 時顯示
    // q177 (B14a_2 植牙)：q175 有「固定式假牙」(option 2) 或 q176 = 1 (部分活動式) 時顯示
    q174: '{q173} = 2',
    q175: '{q173} = 2 or {q173} = 3',
    q176: '({q173} = 2 or {q173} = 3) and ({q175} anyof [1]) and ({q175} notcontains ["none"])',
    q177: '({q173} = 2 or {q173} = 3) and (({q175} anyof [2]) or ({q176} = 1)) and ({q175} notcontains ["none"])',
    // B15 生育子女數（q180=header，依育齡 16-47 顯示）
    q180: '({q8} >= 16 and {q8} <= 47) or ({q12} >= 16 and {q12} <= 47) or ({q13} >= 16 and {q13} <= 47)',
    q181: '{q180} = 2',
    q182: '{q180} = 2',
    q183: '{q180} = 2',
    // B15b 理想子女數：限 18-49 歲
    q184: '({q8} >= 18 and {q8} <= 49) or ({q12} >= 18 and {q12} <= 49) or ({q13} >= 18 and {q13} <= 49)',
    q185: '(({q8} >= 18 and {q8} <= 49) or ({q12} >= 18 and {q12} <= 49) or ({q13} >= 18 and {q13} <= 49)) and ({q184} = 2)',
    q186: '(({q8} >= 18 and {q8} <= 49) or ({q12} >= 18 and {q12} <= 49) or ({q13} >= 18 and {q13} <= 49)) and ({q184} = 2)',
    // B16 月經（q187 女性）
    q187: '{q14} = 2',
    // B17 懷孕（q188=B17a, q189=B17b, q190=B17b_1, q191=B17b_2）
    q188: isWantsPregnant,
    q189: isWantsPregnant + ' and ({q188} = 2 or {q188} = 3)',
    q190: isWantsPregnant + ' and ({q188} = 2 or {q188} = 3) and ({q189} = 2)',
    q191: isWantsPregnant + ' and ({q188} = 2 or {q188} = 3) and ({q189} = 1)',
    // B18 更年期（q192=header, q193=B18b, q199=B18c, q200=B18d, q201=B18e）
    q192: isPostmenopausal,
    q193: isPostmenopausal,
    q194: isPostmenopausal + ' and ({q193} = 2)',
    q195: isPostmenopausal + ' and ({q193} = 2)',
    q196: isPostmenopausal + ' and ({q193} = 2) and ({q195} = 2)',
    q197: isPostmenopausal + ' and ({q193} = 2)',
    q198: isPostmenopausal + ' and ({q193} = 2) and ({q197} = 2)',
    q199: isPostmenopausal + ' and ({q193} = 1 or {q193} = 2 or {q193} = 4)',
    q200: isPostmenopausal + ' and ({q193} = 1 or {q193} = 2 or {q193} = 4)',
    q201: isPostmenopausal + ' and ({q193} = 1 or {q193} = 2 or {q193} = 4)',
    // B19（q202=header, q203=B19a_1, q204=B19a_2）
    q203: '{q202} = 1',
    q204: '{q202} = 2 or {q202} = 3 or {q202} = 4',
    // C1（q209=header C, q210=C1a）
    q210: '{q209} = 3',
    // C2 注意力測試題
    q218: '{q217} = 2',
    q220: '{q219} = 2',
    q222: '{q221} = 2',
    q224: '{q223} = 2',
    // C4（q225=C4, q226=C4a, q227=C4b）
    q226: '(({q8} >= 1 and {q8} <= 25) or ({q12} >= 1 and {q12} <= 25) or ({q13} >= 1 and {q13} <= 25)) and {q225} = 2',
    q227: '{q225} = 2 and {q226} = 1',
    // C5（q228=C5, q229=C5a, q230=C5b）
    q229: '{q228} = 2',
    q230: '{q228} = 1',
    // C6（q231=C6, q232=C6a, q233=C6b）
    q232: '{q231} = 2',
    q233: '{q231} = 2',
    // C7（q234=C7, q235=C7a）
    q235: '{q234} = 2',
    // C8（q236=C8 起算）
    q236: '(({q8} >= 21 and {q8} <= 25) or ({q12} >= 21 and {q12} <= 25) or ({q13} >= 21 and {q13} <= 25)) and {q14} = 2',
    q237: '((({q8} >= 1 and {q8} <= 20) or ({q12} >= 1 and {q12} <= 20) or ({q13} >= 1 and {q13} <= 20)) and {q14} = 2) or ({q236} = 2)',
    q238: '{q237} = 2',
    q239: '({q237} = 1) or ({q238} = 3 or {q238} = 4 or {q238} = 5)',
    q240: '({q237} = 3 or {q237} = 4 or {q237} = 5) or ({q238} = 3 or {q238} = 4 or {q238} = 5)',
    q241: '{q240} = 2',
    // C9（q242=C9 HPV 疫苗、q243=C9a 抹片）
    q242: '({q8} >= 20 and {q8} <= 47) or ({q12} >= 20 and {q12} <= 47) or ({q13} >= 20 and {q13} <= 47)',
    q243: '(({q8} >= 1 and {q8} <= 35) or ({q12} >= 1 and {q12} <= 35) or ({q13} >= 1 and {q13} <= 35)) and {q14} = 2',
    q244: '{q243} = 2',
    q245: '{q243} = 1 or {q244} = 4 or {q244} = 5',
    q246: '(({q8} >= 1 and {q8} <= 35) or ({q12} >= 1 and {q12} <= 35) or ({q13} >= 1 and {q13} <= 35)) and {q14} = 2',
    q247: '{q246} = 2',
    // C10 大腸癌
    q248: '({q8} >= 1 and {q8} <= 15) or ({q12} >= 1 and {q12} <= 15) or ({q13} >= 1 and {q13} <= 15)',
    q249: '{q248} = 2',
    q250: '{q248} = 1',
    q251: '({q8} >= 1 and {q8} <= 25) or ({q12} >= 1 and {q12} <= 25) or ({q13} >= 1 and {q13} <= 25)',
    q252: '{q251} = 2',
    // C11 肺癌
    q253: '({q8} >= 1 and {q8} <= 45) or ({q12} >= 1 and {q12} <= 45) or ({q13} >= 1 and {q13} <= 45)',
    q254: '{q253} = 2',
    q255: '{q253} = 2',
    q256: '{q253} = 2',
    // D1 喝酒（q257=曾否喝酒, q258=D1a 年齡, q259=D1b 最近, q260=D1b_1 一年多久一次）
    q258: '{q257} = 2',
    q259: '{q257} = 2',
    q260: '({q257} = 2) and ({q259} = 1 or {q259} = 2 or {q259} = 3 or {q259} = 4)',
    // D1c 過去一個月飲酒：每輪 a(酒名)/b(容器)/c(數量)/d(是否還有其他)，最多 5 輪
    // 第 1 輪在 isRecentDrinker 時直接顯示；第 2~5 輪由前一輪 d=2(是) 觸發
    q261: isRecentDrinker, // D1c_1a 第一個主要酒名
    q262: isRecentDrinker, // D1c_1b 容器
    q263: isRecentDrinker, // D1c_1c 數量
    q264: isRecentDrinker, // D1c_1d 是否還有其他
    q265: isRecentDrinker + ' and ({q264} = 2)', // D1c_2a 第二多酒名
    q266: isRecentDrinker + ' and ({q264} = 2)', // D1c_2b
    q267: isRecentDrinker + ' and ({q264} = 2)', // D1c_2c
    q268: isRecentDrinker + ' and ({q264} = 2)', // D1c_2d
    q269: isRecentDrinker + ' and ({q268} = 2)', // D1c_3a
    q270: isRecentDrinker + ' and ({q268} = 2)',
    q271: isRecentDrinker + ' and ({q268} = 2)',
    q272: isRecentDrinker + ' and ({q268} = 2)', // D1c_3d
    q273: isRecentDrinker + ' and ({q272} = 2)', // D1c_4a
    q274: isRecentDrinker + ' and ({q272} = 2)',
    q275: isRecentDrinker + ' and ({q272} = 2)',
    q276: isRecentDrinker + ' and ({q272} = 2)', // D1c_4d
    q277: isRecentDrinker + ' and ({q276} = 2)', // D1c_5a
    q278: isRecentDrinker + ' and ({q276} = 2)',
    q279: isRecentDrinker + ' and ({q276} = 2)',
    q280: isRecentDrinker + ' and ({q276} = 2)', // D1c_5d
    // D1d 過去一個月喝最多的一次：5 輪
    q281: isRecentDrinker, // D1d_1a
    q282: isRecentDrinker,
    q283: isRecentDrinker,
    q284: isRecentDrinker, // D1d_1d
    q285: isRecentDrinker + ' and ({q284} = 2)', // D1d_2a
    q286: isRecentDrinker + ' and ({q284} = 2)',
    q287: isRecentDrinker + ' and ({q284} = 2)',
    q288: isRecentDrinker + ' and ({q284} = 2)', // D1d_2d
    q289: isRecentDrinker + ' and ({q288} = 2)', // D1d_3a
    q290: isRecentDrinker + ' and ({q288} = 2)',
    q291: isRecentDrinker + ' and ({q288} = 2)',
    q292: isRecentDrinker + ' and ({q288} = 2)', // D1d_3d
    q293: isRecentDrinker + ' and ({q292} = 2)', // D1d_4a
    q294: isRecentDrinker + ' and ({q292} = 2)',
    q295: isRecentDrinker + ' and ({q292} = 2)',
    q296: isRecentDrinker + ' and ({q292} = 2)', // D1d_4d
    q297: isRecentDrinker + ' and ({q296} = 2)', // D1d_5a
    q298: isRecentDrinker + ' and ({q296} = 2)',
    q299: isRecentDrinker + ' and ({q296} = 2)',
    q300: isRecentDrinker + ' and ({q296} = 2)', // D1d_5d
    // D1e ~ D1k (NEW q301~q308)
    q301: isRecentDrinker,
    q302: '({q257} = 2) and ({q259} = 1 or {q259} = 2 or {q259} = 3 or {q259} = 4)',
    q303: '({q257} = 2) and ({q259} = 1 or {q259} = 2 or {q259} = 3 or {q259} = 4) and ({q302} = 2 or {q302} = 3)',
    q304: '({q257} = 2) and ({q259} = 1 or {q259} = 2 or {q259} = 3 or {q259} = 4)',
    q305: '({q257} = 2) and ({q259} = 1 or {q259} = 2 or {q259} = 3 or {q259} = 4) and ({q304} = 2)',
    // D2 吸菸（q309=D2, q310=D2a, q311=D2a_1, q312=D2a_2a, q313=D2a_2b, q314=D2b, q315=D2c, q316=D2d）
    q310: '{q309} = 2 or {q309} = 3',
    q311: '({q309} = 2 or {q309} = 3) and ({q310} = 2)',
    q312: '({q309} = 2 or {q309} = 3) and ({q310} = 1)',
    q313: '({q309} = 2 or {q309} = 3) and ({q310} = 2)',
    q314: '({q309} = 2 or {q309} = 3)',
    q315: '({q309} = 2 or {q309} = 3) and ({q314} = "other")',
    q316: '({q309} = 2 or {q309} = 3) and ({q314} = "other") and ({q315} = 2)',
    // D2e 其他菸品（q317~q338）
    q318: '{q317} = 2 or {q317} = 3',
    q319: '({q317} = 2 or {q317} = 3) and ({q318} = 2 or {q318} = 3)',
    q321: '{q320} = 2 or {q320} = 3',
    q322: '({q320} = 2 or {q320} = 3) and ({q321} = 2 or {q321} = 3)',
    q324: '{q323} = 2 or {q323} = 3',
    q325: '({q323} = 2 or {q323} = 3) and ({q324} = 2 or {q324} = 3)',
    q327: '{q326} = 2 or {q326} = 3',
    q328: '({q326} = 2 or {q326} = 3) and ({q327} = 2 or {q327} = 3)',
    q330: '{q329} = 2 or {q329} = 3',
    q331: '({q329} = 2 or {q329} = 3) and ({q330} = 2 or {q330} = 3)',
    q333: '{q332} = 2 or {q332} = 3',
    q334: '({q332} = 2 or {q332} = 3) and ({q333} = 2 or {q333} = 3)',
    q336: '{q335} = "other"',
    q337: '{q335} = "other"',
    q338: '({q335} = "other") and ({q337} = 2 or {q337} = 3)',
    // D3 二手菸（q339=D3, q340=D3a, q341=D3a_1, q342=D3b, ...）
    q340: '{q339} = 2',
    q341: '{q339} = 2 and {q340} = 2',
    q342: '{q339} = 2',
    q343: '{q339} = 2',
    q344: '{q339} = 2',
    q345: '{q339} = 2',
    q346: '{q339} = 2',
    // D4 嚼檳榔（q347=D4, q348=D4_1）
    q348: '{q347} = 3',
    q349: '({q347} = 3) and ({q348} = 1)',
    q350: '({q347} = 3) and ({q348} = 1)',
    q351: '({q347} = 3) and ({q348} = 1)',
    q352: '({q347} = 3) and ({q348} = 1)',
    q353: '({q347} = 3) and ({q348} = 1)',
    q354: '({q347} = 3) and ({q348} = 2)',
    q355: '({q347} = 3) and ({q348} = 2) and ({q354} = 2 or {q354} = 3 or {q354} = 4 or {q354} = 5)',
    q356: '({q347} = 3) and ({q348} = 2) and ({q354} = 2 or {q354} = 3 or {q354} = 4 or {q354} = 5) and ({q355} = 1)',
    q357: '({q347} = 3) and ({q348} = 2)',
    q358: '({q347} = 3) and ({q348} = 2)',
    q359: '({q347} = 3) and ({q348} = 2) and ({q358} = 2)',
    q360: '({q347} = 3) and ({q348} = 2)',
    q361: '({q347} = 3) and ({q348} = 2)',
    q362: '({q347} = 3) and ({q348} = 2) and ({q361} = 2)',
    q363: '({q347} = 3) and ({q348} = 2) and ({q361} = 2)',
    q364: '({q347} = 3) and ({q348} = 2) and ({q361} = 2)',
    q365: '({q347} = 3) and ({q348} = 2)',
    q366: '({q347} = 3) and ({q348} = 2)',
    // D5 口腔黏膜（q369=D5a, q370=D5a_1, q371=D5b）
    q369: '(({q8} >= 1 and {q8} <= 35) or ({q12} >= 1 and {q12} <= 35) or ({q13} >= 1 and {q13} <= 35)) and ({q309} = 3 or {q347} = 3) or (({q8} >= 35 and {q8} <= 47) or ({q12} >= 35 and {q12} <= 47) or ({q13} >= 35 and {q13} <= 47)) and ({q347} = 3) and ({q18} = 2)',
    q370: '{q369} = 2',
    q371: '({q369} = 1) or ({q369} = 2 and ({q370} = 3 or {q370} = 4 or {q370} = 5))',
    // D6 腰圍/身高/體重（q372=D6a, q373=D6a-1, q374=D6b, q375=D6b_1）
    q373: '{q372} = 1',
    q375: '{q374} = 2',
    // D7 體重控制（q379=D7, q380=D7a）
    q380: '{q379} = 1 or {q379} = 2',
    // D8 蔬果（q381=D8a, q382=D8b, q383=D8c, q384=D8d）
    q382: '{q381} = 2 or {q381} = 3 or {q381} = 4 or {q381} = 5 or {q381} = 6 or {q381} = 7 or {q381} = 8',
    q384: '{q383} = 2 or {q383} = 3 or {q383} = 4 or {q383} = 5 or {q383} = 6 or {q383} = 7 or {q383} = 8',
    // D10 身體活動（q386=D10a, q387=D10a_1, q388=D10a_2, q389=D10b, q390=D10b_1, q391=D10b_2）
    q387: '{q386} = 2',
    q388: '{q386} = 2',
    q390: '{q389} = 2',
    q391: '{q389} = 2',
    q393: '{q392} = 2',
    q394: '{q393} = 2 or {q393} = 3 or {q393} = 4 or {q393} = 5 or {q393} = 6 or {q393} = 7 or {q393} = 8',
    q395: '{q392} = 2',
    q396: '{q395} = 2 or {q395} = 3 or {q395} = 4 or {q395} = 5 or {q395} = 6 or {q395} = 7 or {q395} = 8',
    q398: '{q397} = 2',
    q399: '{q397} = 2',
    q401: '{q400} = 2',
    q402: '{q400} = 2',
    // D12 咳嗽發燒（q404=D12, q405=D12a, q406=D12b, q407=D12c）
    q405: '{q404} = 2',
    q406: '{q404} = 2',
    q407: '({q404} = 2) and ({q406} = 1 or {q406} = 2 or {q406} = 3 or {q406} = 4 or {q406} = 6)',
    // G1 工作（NEW q419=G1, q420=G1_1, q421=G1_2, q422=G1_3, ...）
    q420: '{q419} = 1',
    q421: '{q419} = 2',
    q422: '{q419} = 3',
    q423: '{q419} = 3',
    q424: '{q419} = 3',
    q425: '{q419} = 3',
    q426: '{q419} = 3',
    q427: '({q419} = 3) and ({q426} = 1)',
    q428: '({q419} = 3) and ({q426} = 2 or {q426} = 4)',
    q429: '{q419} = 3',
    // G2 健保（q430=G2, q431=G2a）
    q431: '{q430} = 1 or {q430} = 3 or {q430} = 4',
    // G4（q434=G4, q435=G4a, q436=G4b）
    q435: '{q434} = 2',
    // G6 → G6a 電梯：選 1 公寓(5樓以下) 或 2 透天 才顯示
    q439: '{q438} = 1 or {q438} = 2 or {q438} = "other"',
    // I0 → 選 6 都不想要 → 跳至 I2，隱藏 I1 系列
    q445: '{q444} notcontains ["none"]',
    q446: '{q445} = 2',
    q447: '{q445} = 2',
  })

  // 非必填（1-based 寫法，convertNotRequired 會 -1）
  // q416 = E6 拆兩題的「第1題文字」，僅顯示題目說明，不需作答
  const notRequired = convertNotRequired([416])

  const mappedRateChoice = null
  const pageBreakQuestions = null

  const triggersConditionsInit = convertTriggers([])
  const questionMappings = convertQuestionMappings({})

  const transformSurveyData = useMemo(() => {
    if (!birthDate) return undefined
    const replacement = `${birthDate.rocYear}年${birthDate.month}月${birthDate.day}日`
    return (data) => ({
      ...data,
      data: (data.data || []).map((q) => ({
        ...q,
        ma: {
          ...q.ma,
          content:
            typeof q.ma?.content === 'string'
              ? q.ma.content.split(BIRTH_DATE_PLACEHOLDER).join(replacement)
              : q.ma?.content,
        },
      })),
    })
  }, [birthDate])

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    transformSurveyData,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  // 結尾語：國健署宣導
  const endMessage = `<div>我們的訪問到這邊告一段落，非常謝謝您的協助！<br><br>♥國民健康署祝您健康快樂♥<br><br><br>相關健康資訊可至國民健康署網站查詢<br><br>(1)可選下方按鈕，自行留存「受訪者同意書」。<br><a href="#" onclick="return false;" style="display:inline-block;padding:8px 16px;background-color:#4CAF50;color:#fff;text-decoration:none;border-radius:4px;margin-top:8px;">下載同意書留存</a><br><br>(2)<span style="color:#EA0000">好禮抽抽週活動</span>，請詳見活動說明網頁：<br>👉<a href="/P260601122243471/lottery" target="_blank">${window.location.origin}/P260601122243471/lottery</a></div>`

  function helpFunction(survey, options) {}

  const endSurveyMsg = (survey) => {
    setCompletionMessage(endMessage)
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving: requiresLogin.login || webAuthType > 0,
    webAuthType,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
